import { getAndClearCodeVerifier, getProviderId, verifyAndClearState } from '@auth/utils';

interface CallbackParams {
  code: string;
  providerId: string;
  codeVerifier: string;
  state: string;
}

/**
 * Validates OAuth callback parameters and returns validated data
 * @param searchParams - URL search parameters from the callback
 * @returns Validated callback parameters
 * @throws Error if validation fails
 */
export function validateCallbackParams(searchParams: URLSearchParams): CallbackParams {
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  // Check for errors from the auth provider
  if (error) {
    throw new Error(`Authentication error: ${error}${errorDescription ? ` - ${errorDescription}` : ''}`);
  }

  // Validate required parameters
  if (!code || !state) {
    throw new Error('Missing required parameters (code or state)');
  }

  // Verify the state parameter to prevent CSRF attacks
  if (!verifyAndClearState(state)) {
    throw new Error('Invalid state parameter');
  }

  // Get the code verifier and provider ID from session storage
  const codeVerifier = getAndClearCodeVerifier();
  const providerId = getProviderId();

  if (!codeVerifier) {
    throw new Error('Code verifier not found');
  }

  if (!providerId) {
    throw new Error('Provider ID not found');
  }

  return { code, providerId, codeVerifier, state };
}
